/**
 * Configs, templates, scratch copies and loads
 * (docs/wiring-documentation-proposal.md, "Configs, templates and scratch
 * copies", "Loading a config", "Lock and pushes").
 *
 * Shared by the API (load endpoint, bridge-sync), the Zero mutators, the
 * bridge (load orchestrator) and the UI.
 */
import { rewriteMediaAndPlayoutDevices } from "./config-playout.js";
import { LM_IO_ENTRY_KINDS, LM_IO_ENTRY_LABELS } from "./lm-io-server.js";
import { VMIX_OUTPUT_LABELS } from "./vmix-outputs.js";
import { portLabel } from "./wiring.js";
export const STUDIO_CONFIG_KINDS = ["config", "template", "scratch"];
export const LOAD_MODES = ["full", "setup"];
export const LOAD_MODE_LABELS = {
    full: "Full",
    setup: "Setup only",
};
/**
 * A load the bridge hasn't started within this time expires: a Full load
 * must not run whenever an OB van happens to power up again.
 */
export const CONFIG_LOAD_PENDING_EXPIRY_MS = 10 * 60 * 1000;
/** A started load whose bridge stopped reporting for this long expires. */
export const CONFIG_LOAD_RUNNING_STALE_MS = 30 * 60 * 1000;
/** Whether a load is still open (the bridge may still run or report it). */
export function isConfigLoadOpen(load, now) {
    if (load.status === "running")
        return true;
    if (load.status !== "pending")
        return false;
    return (load.createdAt == null ||
        now - load.createdAt < CONFIG_LOAD_PENDING_EXPIRY_MS);
}
/** Scratch copies that are not active, not kept and older than this go. */
export const SCRATCH_RETENTION_DAYS = 30;
/** A push merges into the latest bridge version if it is younger than this. */
export const CONFIG_VERSION_COALESCE_MS = 5 * 60 * 1000;
/**
 * The parts of `config_data` the bridge owns and pushes. While a config is
 * active they are locked in the cloud.
 */
export const BRIDGE_OWNED_CONFIG_KEYS = [
    "layouts",
    "device_states",
    "dante",
    "media_pool",
    "playout",
];
/**
 * The parts of `config_data` only the cloud edits. The bridge does not know
 * them, so its pushes keep the cloud's copy (`mergeCloudOwnedConfigData`),
 * and the lock doesn't cover them.
 */
export const CLOUD_OWNED_CONFIG_KEYS = [
    "lm_io_server",
    "vmix_outputs",
];
/**
 * A bridge push with the cloud-owned parts of `current` (the config's data
 * before the push) kept: the bridge's export never contains them.
 */
export function mergeCloudOwnedConfigData(current, pushed) {
    if (!current || typeof pushed !== "object" || pushed === null)
        return pushed;
    let next = pushed;
    for (const key of CLOUD_OWNED_CONFIG_KEYS) {
        if (current[key] !== undefined && pushed[key] === undefined) {
            next = { ...next, [key]: current[key] };
        }
    }
    return next;
}
export const CONFIG_LOCK_MESSAGE = "Active on the studio's bridge. Change it live on the devices, or load another config first.";
export const LOAD_STEPS = [
    "fetch",
    "validate",
    "database",
    "devices",
    "videohub",
    "atem",
    "dante",
    "media",
];
/** Combine the step lines into the reported result. */
export function aggregateLoadResult(input) {
    const count = (status) => input.steps
        .filter((step) => step.status === status)
        .reduce((sum, step) => sum + Math.max(1, step.messages.length), 0);
    const warnings = count("warning");
    const errors = count("error");
    return {
        status: errors > 0 ? "error" : warnings > 0 ? "warning" : "ok",
        applied: input.applied,
        mode: input.mode,
        warnings,
        errors,
        steps: input.steps,
        started_at: input.startedAt.toISOString(),
        finished_at: input.finishedAt.toISOString(),
    };
}
function plural(count, word) {
    return `${count} ${word}${count === 1 ? "" : "s"}`;
}
/** "Loaded v7", "Loaded v7 with 2 warnings", "Load of v7 failed: …". */
export function summarizeLoadResult(version, result) {
    if (!result)
        return `Loading v${version}…`;
    if (!result.applied) {
        const failed = result.steps.find((step) => step.status === "error");
        return `Load of v${version} failed${failed?.messages[0] ? `: ${failed.messages[0]}` : ""}`;
    }
    const parts = [];
    if (result.errors > 0)
        parts.push(plural(result.errors, "error"));
    if (result.warnings > 0)
        parts.push(plural(result.warnings, "warning"));
    return parts.length > 0
        ? `Loaded v${version} with ${parts.join(" and ")}`
        : `Loaded v${version}`;
}
// ─── Scratch copies ────────────────────────────────────────────
function pad(value) {
    return String(value).padStart(2, "0");
}
/** "Default template (scratch 19.10. 14:30)", in the studio's time zone. */
export function scratchConfigName(originalName, now, timeZone = "Europe/Zurich") {
    const parts = Object.fromEntries(new Intl.DateTimeFormat("de-CH", {
        timeZone,
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    })
        .formatToParts(now)
        .map((part) => [part.type, part.value]));
    const stamp = `${pad(Number(parts.day))}.${pad(Number(parts.month))}. ${parts.hour}:${parts.minute}`;
    const base = originalName.replace(/\s*\(scratch [^)]*\)$/, "");
    return `${base} (scratch ${stamp})`.slice(0, 255);
}
/** Whether a scratch copy is due for the daily clean-up. */
export function isScratchExpired(config, activeConfigIDs, configID, now) {
    if (config.kind !== "scratch" || config.kept)
        return false;
    if (activeConfigIDs.has(configID))
        return false;
    const lastUsed = Math.max(config.updatedAt ?? 0, config.lastLoadedAt ?? 0, config.lastActiveAt ?? 0);
    return now - lastUsed > SCRATCH_RETENTION_DAYS * 24 * 60 * 60 * 1000;
}
function isOpenConflict(row) {
    return row.conflictBaseVersion != null && row.conflictResolvedAt == null;
}
/**
 * Where a write of new config data goes (docs "Lock and pushes"):
 *
 * - A write based on the config's current version (`baseVersion` =
 *   `headVersion`, or no base from old clients) becomes the next version. It
 *   merges into the current version only if that one came from the same
 *   source, was never loaded, is no open conflict and is younger than the
 *   coalesce window: consecutive bridge pushes since the last load merge,
 *   a loaded version is never overwritten.
 * - A write based on another version (after a force unlock, or "Save to
 *   original" when the original changed) is stored as a conflict version
 *   and leaves the current version alone. Further bridge pushes based on
 *   that conflict version merge into it.
 */
export function planConfigWrite(input) {
    const window = input.coalesceMs ?? CONFIG_VERSION_COALESCE_MS;
    const maxVersion = input.versions.reduce((max, row) => Math.max(max, row.version), input.headVersion);
    const base = input.baseVersion ?? input.headVersion;
    if (base === input.headVersion) {
        const head = input.versions.find((row) => row.version === input.headVersion);
        if (head &&
            head.version === maxVersion &&
            head.source === input.source &&
            head.loadedAt == null &&
            !isOpenConflict(head) &&
            input.now - head.createdAt < window) {
            return { kind: "coalesce", versionID: head.id, version: head.version };
        }
        return { kind: "new", version: maxVersion + 1 };
    }
    const based = input.versions.find((row) => row.version === base);
    if (input.source === "bridge" &&
        based &&
        based.version === maxVersion &&
        based.source === "bridge" &&
        based.loadedAt == null &&
        isOpenConflict(based)) {
        return {
            kind: "coalesce-conflict",
            versionID: based.id,
            version: based.version,
        };
    }
    return {
        kind: "conflict",
        version: maxVersion + 1,
        conflictBaseVersion: base,
    };
}
// ─── Lock ──────────────────────────────────────────────────────
/** The active config is locked unless an admin force-unlocked it. */
export function isConfigLocked(studio, configID) {
    return (!!studio &&
        studio.activeConfigID === configID &&
        studio.activeConfigUnlockedAt == null);
}
function stableJson(value) {
    if (Array.isArray(value))
        return `[${value.map(stableJson).join(",")}]`;
    if (value && typeof value === "object") {
        return `{${Object.keys(value)
            .filter((key) => value[key] !== undefined)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`)
            .join(",")}}`;
    }
    return JSON.stringify(value ?? null);
}
/** The bridge-owned parts that differ between two config data blobs. */
export function changedBridgeOwnedKeys(before, after) {
    return BRIDGE_OWNED_CONFIG_KEYS.filter((key) => stableJson(before?.[key]) !== stableJson(after?.[key]));
}
function rewriteLmIoPorts(data, map) {
    const setup = data.lm_io_server;
    if (!setup)
        return data;
    const rewrite = (entries) => (entries ?? []).map((entry) => entry.port_id && map.has(entry.port_id)
        ? { ...entry, port_id: map.get(entry.port_id) ?? null }
        : entry);
    return {
        ...data,
        lm_io_server: {
            ...setup,
            sources: rewrite(setup.sources),
            outputs: rewrite(setup.outputs),
        },
    };
}
/**
 * Every place in config data that holds a port ID. Each entry rewrites its
 * references through `map` (IDs not in the map stay as they are, and
 * validation warns about dangling ones).
 */
export const CONFIG_PORT_REFERENCE_LOCATIONS = [
    {
        // The ATEM input where vMix cut-ins arrive
        name: "playout.settings.cutin_port_id",
        collect: (data) => data.playout?.settings?.cutin_port_id
            ? [
                {
                    portID: data.playout.settings.cutin_port_id,
                    usage: "Playout cut-in input",
                    use: "receive",
                },
            ]
            : [],
        rewrite: (data, map) => {
            const portID = data.playout?.settings?.cutin_port_id;
            if (!data.playout || !portID || !map.has(portID))
                return data;
            return {
                ...data,
                playout: {
                    ...data.playout,
                    settings: {
                        ...data.playout.settings,
                        cutin_port_id: map.get(portID) ?? null,
                    },
                },
            };
        },
    },
    {
        // lm-io-server sources (captured) and outputs (played out)
        name: "lm_io_server.{sources,outputs}[].port_id",
        collect: (data) => LM_IO_ENTRY_KINDS.flatMap((kind) => (data.lm_io_server?.[kind] ?? [])
            .filter((entry) => !!entry.port_id)
            .map((entry) => ({
            portID: entry.port_id,
            usage: `lm-io ${LM_IO_ENTRY_LABELS[kind]} "${entry.name}"`,
            use: kind === "sources" ? "receive" : "send",
        }))),
        rewrite: rewriteLmIoPorts,
    },
    {
        // The vMix output each DeckLink port shows (signal tracing)
        name: "vmix_outputs.<port_id>",
        collect: (data) => Object.entries(data.vmix_outputs ?? {}).map(([portID, output]) => ({
            portID,
            usage: `vMix ${VMIX_OUTPUT_LABELS[output] ?? output}`,
            use: "send",
        })),
        rewrite: (data, map) => {
            const setup = data.vmix_outputs;
            if (!setup || !Object.keys(setup).some((portID) => map.has(portID))) {
                return data;
            }
            const next = {};
            for (const [portID, output] of Object.entries(setup)) {
                const mapped = map.has(portID) ? map.get(portID) : portID;
                if (mapped)
                    next[mapped] = output;
            }
            return { ...data, vmix_outputs: next };
        },
    },
];
/** Every port reference in config data, with its use. */
export function configPortReferenceUses(data) {
    return CONFIG_PORT_REFERENCE_LOCATIONS.flatMap((location) => location.collect(data));
}
/** All port IDs config data references. */
export function configPortReferences(data) {
    return configPortReferenceUses(data).map((reference) => reference.portID);
}
/**
 * Rewrite device IDs (keys of `device_states`) and port references of a
 * copy. Devices missing from `deviceMap` keep their ID when `keepUnmapped`
 * (same studio), otherwise their state is dropped and listed.
 */
export function rewriteConfigReferences(data, options) {
    const droppedDeviceIDs = [];
    let next = { ...data };
    if (data.device_states) {
        const states = {};
        for (const [deviceID, state] of Object.entries(data.device_states)) {
            const mapped = options.deviceMap?.get(deviceID);
            const target = mapped !== undefined ? mapped : options.keepUnmapped ? deviceID : null;
            if (target)
                states[target] = state;
            else
                droppedDeviceIDs.push(deviceID);
        }
        next = { ...next, device_states: states };
    }
    next = rewriteMediaAndPlayoutDevices(next, options.deviceMap, options.keepUnmapped);
    const lmIoDevice = next.lm_io_server?.device_id;
    if (next.lm_io_server && lmIoDevice) {
        const mapped = options.deviceMap?.get(lmIoDevice);
        next = {
            ...next,
            lm_io_server: {
                ...next.lm_io_server,
                device_id: mapped !== undefined
                    ? mapped
                    : options.keepUnmapped
                        ? lmIoDevice
                        : null,
            },
        };
    }
    const portMap = options.portMap ?? new Map();
    for (const location of CONFIG_PORT_REFERENCE_LOCATIONS) {
        next = location.rewrite(next, portMap);
    }
    return { data: next, droppedDeviceIDs };
}
/**
 * Match the source studio's devices to the target studio's for a copy to
 * another studio: same driver and name, else the only device with that
 * driver. Unmatched devices map to null (their state is dropped).
 */
export function autoMapDevices(source, target) {
    const map = new Map();
    const used = new Set();
    const norm = (name) => name.trim().toLowerCase();
    for (const device of source) {
        const byName = target.find((candidate) => !used.has(candidate.id) &&
            candidate.connectionType === device.connectionType &&
            norm(candidate.name) === norm(device.name));
        if (byName) {
            map.set(device.id, byName.id);
            used.add(byName.id);
        }
    }
    for (const device of source) {
        if (map.has(device.id))
            continue;
        const sameDriver = target.filter((candidate) => candidate.connectionType === device.connectionType);
        const sourceSameDriver = source.filter((other) => other.connectionType === device.connectionType);
        if (sameDriver.length === 1 &&
            sourceSameDriver.length === 1 &&
            !used.has(sameDriver[0].id)) {
            map.set(device.id, sameDriver[0].id);
            used.add(sameDriver[0].id);
        }
        else {
            map.set(device.id, null);
        }
    }
    return map;
}
// ─── Validation ────────────────────────────────────────────────
/**
 * Check a config's references against the studio's effective wiring
 * (`effectiveWiring`: permanent plus the effective config's own gear and
 * re-patches) before a load. Warnings only: a config must stay loadable
 * after a re-cabling or after a device was removed. Scoped wiring is not
 * versioned with the config, so an older version may point at ports that
 * are gone.
 */
export function validateConfigReferences(data, installation) {
    const warnings = [];
    if (installation.effectiveConfigID) {
        const linked = new Set(installation.links.flatMap((link) => [link.fromPortID, link.toPortID]));
        for (const device of installation.devices) {
            if (device.configID !== installation.effectiveConfigID ||
                device.kind === "software") {
                continue;
            }
            const own = installation.ports.filter((port) => port.deviceID === device.id);
            if (own.length > 0 && !own.some((port) => linked.has(port.id))) {
                warnings.push(`Temporary gear ${device.name} has no links`);
            }
        }
    }
    const deviceIDs = new Set(installation.devices.map((device) => device.id));
    for (const deviceID of Object.keys(data.device_states ?? {})) {
        if (!deviceIDs.has(deviceID)) {
            warnings.push(`State for a device that is no longer in the studio (${deviceID}) is not applied`);
        }
    }
    for (const deviceID of Object.keys(data.media_pool ?? {})) {
        if (!deviceIDs.has(deviceID)) {
            warnings.push(`Media pool of a device that is no longer in the studio (${deviceID}) is not restored`);
        }
    }
    const settings = data.playout?.settings;
    for (const [label, deviceID] of [
        ["ATEM", settings?.atem_device_id],
        ["vMix", settings?.vmix_device_id],
    ]) {
        if (deviceID && !deviceIDs.has(deviceID)) {
            warnings.push(`Playout uses a ${label} that is no longer in the studio (${deviceID})`);
        }
    }
    const videoCutins = (data.playout?.playouts ?? []).some((playout) => playout.playout_type === "video-cutin");
    if (videoCutins && settings && !settings.cutin_port_id) {
        warnings.push(settings.cutin_atem_input != null
            ? `Playout video cut-ins use ATEM input ${settings.cutin_atem_input}, which is not mapped to a port yet`
            : "Playout has video cut-ins but no cut-in port");
    }
    const portByID = new Map(installation.ports.map((port) => [port.id, port]));
    const linkByPort = new Map();
    for (const link of installation.links) {
        linkByPort.set(link.fromPortID, link);
        linkByPort.set(link.toPortID, link);
    }
    const seen = new Set();
    for (const reference of configPortReferenceUses(data)) {
        const key = `${reference.usage}|${reference.portID}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        const port = portByID.get(reference.portID);
        if (!port) {
            warnings.push(`${reference.usage} uses a port that no longer exists (${reference.portID})`);
            continue;
        }
        const label = referencedPortLabel(port, installation.devices);
        const link = linkByPort.get(port.id);
        if (!link) {
            warnings.push(`${reference.usage} uses ${label}, which is not cabled`);
        }
        else if (reference.use === "receive" && link.toPortID !== port.id) {
            warnings.push(`${reference.usage} uses ${label}, which is cabled as an output`);
        }
        else if (reference.use === "send" && link.fromPortID !== port.id) {
            warnings.push(`${reference.usage} uses ${label}, which is cabled as an input`);
        }
    }
    return warnings;
}
/** "DL5 (DeckLink Quad 2)", or the port ID when only the ID is known. */
function referencedPortLabel(port, devices) {
    if (port.number === undefined || !port.direction)
        return port.id;
    const label = portLabel(port);
    const device = devices.find((d) => d.id === port.deviceID);
    return device ? `${label} (${device.name})` : label;
}
